import { apiClient } from '@/services/api'

declare global {
  interface Window {
    umami?: {
      track: {
        (eventName: string, eventData?: Record<string, any>): void
        (payload: (props: Record<string, any>) => Record<string, any>): void
      }
    }
  }
}

interface TrackingConfig {
  meta_pixel_id: string | null
  ga_measurement_id: string | null
  gtm_id: string | null
}

const DEFAULT_TRACKING_CONFIG: TrackingConfig = {
  meta_pixel_id: null,
  ga_measurement_id: null,
  gtm_id: null,
}

let trackingConfig: TrackingConfig | null = null
let initPromise: Promise<TrackingConfig> | null = null

export async function fetchTrackingConfig(): Promise<TrackingConfig> {
  if (trackingConfig) return trackingConfig
  if (initPromise) return initPromise

  initPromise = (async () => {
    try {
      const json = await apiClient.get<{ data?: Partial<TrackingConfig> }>('/public/tracking-config')
      if (json.data) {
        trackingConfig = { ...DEFAULT_TRACKING_CONFIG, ...json.data }
        return trackingConfig!
      }
    } catch {
      // silent fail
    }
    trackingConfig = { ...DEFAULT_TRACKING_CONFIG }
    return trackingConfig
  })()

  return initPromise
}

export function initPixels(config: TrackingConfig): void {
  if (config.meta_pixel_id) {
    initMetaPixel(config.meta_pixel_id)
  }
  if (config.ga_measurement_id) {
    initGoogleAnalytics(config.ga_measurement_id)
  }
  if (config.gtm_id) {
    initGoogleTagManager(config.gtm_id)
  }
}

export function trackEvent(name: string, params?: Record<string, any>): void {
  trackFbq(name, params)
  trackGtag(name, params)
  trackUmami(name, params)
  trackDataLayer(name, params)
}

// --- Umami ---

export function trackUmami(name: string, params?: Record<string, any>): void {
  if (!window.umami) return
  if (params && Object.keys(params).length > 0) {
    window.umami.track(name, params)
  } else {
    window.umami.track(name)
  }
}

/**
 * Dispara um evento Umami atribuído a OUTRO website (sobrescreve o website id do
 * tracker carregado nesta página). Usado para espelhar eventos do checkout no
 * website do funil do quiz, mesmo sendo domínios/websites distintos.
 */
export function trackUmamiToWebsite(websiteId: string, name: string, data?: Record<string, any>): void {
  if (!window.umami) return
  window.umami.track((props) => ({ ...props, website: websiteId, name, data }))
}

// --- Meta Pixel ---

function initMetaPixel(pixelId: string): void {
  if ((window as any).fbq) return

  const f = window as any
  const n = (f.fbq = function (...args: any[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
  }) as any
  if (!f._fbq) f._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  f.fbq('init', pixelId)
  f.fbq('track', 'PageView')
}

const FB_EVENT_MAP: Record<string, string> = {
  Lead: 'Lead',
  CompleteRegistration: 'CompleteRegistration',
  ViewContent: 'ViewContent',
  InitiateCheckout: 'InitiateCheckout',
  AddPaymentInfo: 'AddPaymentInfo',
  Purchase: 'Purchase',
}

function trackFbq(name: string, params?: Record<string, any>): void {
  const fbq = (window as any).fbq
  if (!fbq) return

  const fbEvent = FB_EVENT_MAP[name]
  if (fbEvent) {
    fbq('track', fbEvent, params)
  } else {
    fbq('trackCustom', name, params)
  }
}

// --- Google Analytics ---

function initGoogleAnalytics(measurementId: string): void {
  if ((window as any).gtag) return

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  const w = window as any
  w.dataLayer = w.dataLayer || []
  w.gtag = function (...args: any[]) {
    w.dataLayer.push(args)
  }
  w.gtag('js', new Date())
  w.gtag('config', measurementId)
}

// --- Google Tag Manager ---

function initGoogleTagManager(containerId: string): void {
  const w = window as any
  if (w.__gtmLoaded) return
  w.__gtmLoaded = true

  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`
  document.head.appendChild(script)
}

function trackDataLayer(name: string, params?: Record<string, any>): void {
  const w = window as any
  if (!w.dataLayer) return
  w.dataLayer.push({ event: name, ...(params ?? {}) })
}

const GA_EVENT_MAP: Record<string, string> = {
  Lead: 'generate_lead',
  CompleteRegistration: 'sign_up',
  ViewContent: 'view_item',
  InitiateCheckout: 'begin_checkout',
  AddPaymentInfo: 'add_payment_info',
  Purchase: 'purchase',
}

function trackGtag(name: string, params?: Record<string, any>): void {
  const gtag = (window as any).gtag
  if (!gtag) return

  const gaEvent = GA_EVENT_MAP[name] || name
  gtag('event', gaEvent, params)
}
